import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { ClipboardList, Plus, Clock, CheckCircle, Loader2, Trash2, Edit, AlertCircle, Calendar, RefreshCw, Building2, Users, Eye, XCircle, Send, FileText, CalendarDays, CalendarRange, Layers, Upload, Download, X, User, ArrowUpDown, ArrowUp, ArrowDown, Archive, RotateCcw } from 'lucide-react';
import { ScrollArea } from '../../components/ui/scroll-area';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BranchTasks = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [dailyTasks, setDailyTasks] = useState([]);
  const [submittedTasks, setSubmittedTasks] = useState([]);
  const [internalTasks, setInternalTasks] = useState([]); // Şube İçi Görevler
  const [employees, setEmployees] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [activeTab, setActiveTab] = useState('daily');
  const [mainTab, setMainTab] = useState('tasks'); // tasks, reports, schedules
  
  // İş Programı state
  const [schedules, setSchedules] = useState([]);
  const [scheduleTab, setScheduleTab] = useState('daily');
  const [createScheduleModalOpen, setCreateScheduleModalOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [branchUsers, setBranchUsers] = useState([]);
  const [newSchedule, setNewSchedule] = useState({
    title: '',
    description: '',
    schedule_type: 'daily',
    start_date: '',
    end_date: '',
    priority: 'normal',
    documents: [],
    send_to_users: []
  });
  
  // Review modal state
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewingTask, setReviewingTask] = useState(null);
  const [hrNotes, setHrNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  
  // Progress modal state
  const [progressModalOpen, setProgressModalOpen] = useState(false);
  const [progressLogs, setProgressLogs] = useState([]);
  const [newProgressLog, setNewProgressLog] = useState({ description: '', documents: [] });
  
  // Completion modal state
  const [completionModalOpen, setCompletionModalOpen] = useState(false);
  const [completionData, setCompletionData] = useState({
    completion_notes: '',
    completion_date: new Date().toISOString().split('T')[0]
  });
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assigned_to: '',
    task_type: 'attendance_tracking',
    subcontractor_ids: [],
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: '',
    is_recurring: false,
    priority: 'normal',
    documents: []
  });
  
  // Task Detail Modal state
  const [taskDetailModalOpen, setTaskDetailModalOpen] = useState(false);
  const [viewingTaskDetail, setViewingTaskDetail] = useState(null);

  // Çoklu seçim state'leri
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // İş Programı sıralama state - varsayılan: son güncelleme (en yeni üstte)
  const [scheduleSortConfig, setScheduleSortConfig] = useState({ key: 'updated_at', direction: 'desc' });
  const [editScheduleModalOpen, setEditScheduleModalOpen] = useState(false);
  
  // YENİ Badge için state'ler - okunmamış bildirim olan öğeler
  const [unreadTaskIds, setUnreadTaskIds] = useState(new Set());
  const [unreadScheduleIds, setUnreadScheduleIds] = useState(new Set());

  useEffect(() => {
    loadData();
    loadUnreadNotifications(); // YENİ badge için bildirimleri yükle
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('fortis_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // YENİ Badge - Okunmamış bildirimleri yükle
  const loadUnreadNotifications = async () => {
    try {
      // GÖREVLER için
      const gorevlerRes = await axios.get(`${API}/notifications`, {
        params: { category: 'gorevler', is_read: false },
        headers: getAuthHeaders()
      });
      const gorevlerIds = new Set((gorevlerRes.data || []).map(n => n.related_id).filter(Boolean));
      setUnreadTaskIds(gorevlerIds);
      
      // İŞ PROGRAMI için
      const isProgramiRes = await axios.get(`${API}/notifications`, {
        params: { category: 'is_programi', is_read: false },
        headers: getAuthHeaders()
      });
      const scheduleIds = new Set((isProgramiRes.data || []).map(n => n.related_id).filter(Boolean));
      setUnreadScheduleIds(scheduleIds);
    } catch (error) {
      console.error('Bildirimler yüklenirken hata:', error);
    }
  };

  // YENİ Badge - Görev bildirimi okundu olarak işaretle
  const markTaskNotificationAsRead = async (taskId) => {
    try {
      const res = await axios.get(`${API}/notifications`, {
        params: { category: 'gorevler', is_read: false },
        headers: getAuthHeaders()
      });
      const relatedNotifs = (res.data || []).filter(n => n.related_id === taskId);
      
      for (const notif of relatedNotifs) {
        await axios.put(`${API}/notifications/${notif.id}/read`, {}, { headers: getAuthHeaders() });
      }
      
      setUnreadTaskIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(taskId);
        return newSet;
      });
    } catch (error) {
      console.error('Bildirim okundu yapılırken hata:', error);
    }
  };

  // YENİ Badge - İş planı bildirimi okundu olarak işaretle
  const markScheduleNotificationAsRead = async (scheduleId) => {
    try {
      const res = await axios.get(`${API}/notifications`, {
        params: { category: 'is_programi', is_read: false },
        headers: getAuthHeaders()
      });
      const relatedNotifs = (res.data || []).filter(n => n.related_id === scheduleId);
      
      for (const notif of relatedNotifs) {
        await axios.put(`${API}/notifications/${notif.id}/read`, {}, { headers: getAuthHeaders() });
      }
      
      setUnreadScheduleIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(scheduleId);
        return newSet;
      });
    } catch (error) {
      console.error('Bildirim okundu yapılırken hata:', error);
    }
  };

  const loadData = async () => {
    try {
      const [tasksRes, dailyRes, empRes, subRes, submittedRes, schedulesRes, usersRes, internalRes] = await Promise.all([
        axios.get(`${API}/branch/tasks`),
        axios.get(`${API}/branch/daily-tasks`),
        axios.get(`${API}/branch/employees`),
        axios.get(`${API}/branch/subcontractors`),
        axios.get(`${API}/branch/submitted-tasks`),
        axios.get(`${API}/work/schedules`, { headers: getAuthHeaders() }),
        axios.get(`${API}/calendar/users-for-share`, { headers: getAuthHeaders() }),
        axios.get(`${API}/branch/internal-tasks`, { headers: getAuthHeaders() })
      ]);
      setTasks(tasksRes.data || []);
      setDailyTasks(dailyRes.data || []);
      setEmployees(empRes.data || []);
      setSubcontractors(subRes.data || []);
      setSubmittedTasks(submittedRes.data || []);
      setSchedules(schedulesRes.data || []);
      setBranchUsers(usersRes.data.users || []);
      setInternalTasks(internalRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Veriler yüklenirken hata oluştu');
    }
    setLoading(false);
  };

  // İş Programı functions
  const loadSchedules = async () => {
    try {
      const res = await axios.get(`${API}/work/schedules`, { headers: getAuthHeaders() });
      setSchedules(res.data || []);
    } catch (error) {
      console.error('İş planları yüklenirken hata:', error);
    }
  };

  const handleDocumentUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        setNewSchedule(prev => ({
          ...prev,
          documents: [...prev.documents, {
            name: file.name,
            type: file.type,
            size: file.size,
            data: reader.result
          }]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removeDocument = (index) => {
    setNewSchedule(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };

  const toggleUserSelection = (userId) => {
    setNewSchedule(prev => {
      const users = prev.send_to_users.includes(userId)
        ? prev.send_to_users.filter(id => id !== userId)
        : [...prev.send_to_users, userId];
      return { ...prev, send_to_users: users };
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const isPreviewable = (type) => {
    return type && (type.startsWith('image/') || type === 'application/pdf');
  };

  // Task file upload handler
  const handleTaskFileUpload = (event) => {
    const files = Array.from(event.target.files);
    files.forEach(file => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Dosya boyutu 10MB\'dan büyük olamaz');
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({
          ...prev,
          documents: [...prev.documents, {
            name: file.name,
            type: file.type,
            size: file.size,
            data: e.target.result
          }]
        }));
      };
      reader.readAsDataURL(file);
    });
    event.target.value = '';
  };

  // Remove task document
  const removeTaskDocument = (index) => {
    setFormData(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };

  // View task details
  const handleViewTaskDetail = (task) => {
    setViewingTaskDetail(task);
    setTaskDetailModalOpen(true);
  };

  const handleCreateSchedule = async () => {
    if (!newSchedule.title) {
      toast.error('İş planı başlığı gerekli');
      return;
    }
    
    try {
      await axios.post(`${API}/work/schedules`, {
        ...newSchedule,
        schedule_type: scheduleTab,
        owner_name: user?.name || 'Bilinmiyor'
      }, { headers: getAuthHeaders() });
      toast.success('İş planı oluşturuldu');
      setCreateScheduleModalOpen(false);
      setNewSchedule({
        title: '',
        description: '',
        schedule_type: scheduleTab,
        start_date: '',
        end_date: '',
        priority: 'normal',
        documents: [],
        send_to_users: []
      });
      loadSchedules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İş planı oluşturulamadı');
    }
  };

  const handleScheduleStatusChange = async (scheduleId, newStatus) => {
    try {
      await axios.put(`${API}/work/schedules/${scheduleId}`, { status: newStatus }, { headers: getAuthHeaders() });
      toast.success('Durum güncellendi');
      loadSchedules();
      setDetailModalOpen(false);
    } catch (error) {
      toast.error('Durum güncellenemedi');
    }
  };

  const handleOpenCompletionModal = (schedule) => {
    setSelectedSchedule(schedule);
    setCompletionData({
      completion_notes: '',
      completion_date: new Date().toISOString().split('T')[0]
    });
    setDetailModalOpen(false);
    setCompletionModalOpen(true);
  };

  const handleCompleteWithDetails = async () => {
    if (!selectedSchedule) return;
    
    if (!completionData.completion_notes.trim()) {
      toast.error('Tamamlama açıklaması gerekli');
      return;
    }
    
    try {
      // Tamamlanma tarihi ve saati otomatik olarak şimdiki zaman
      const completionDateTime = new Date().toISOString();
      
      await axios.put(`${API}/work/schedules/${selectedSchedule.id}`, {
        status: 'completed',
        completion_notes: completionData.completion_notes,
        completion_date: completionDateTime,
        completed_by: user?.id,
        completed_by_name: user?.name
      }, { headers: getAuthHeaders() });
      toast.success('İş planı tamamlandı');
      setCompletionModalOpen(false);
      loadSchedules();
    } catch (error) {
      toast.error('İş planı tamamlanamadı');
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    try {
      await axios.delete(`${API}/work/schedules/${scheduleId}`, { headers: getAuthHeaders() });
      toast.success('İş planı silindi');
      loadSchedules();
    } catch (error) {
      toast.error('İş planı silinemedi');
    }
  };

  // Çoklu seçim fonksiyonları
  const toggleTaskSelection = (taskId) => {
    setSelectedTasks(prev => 
      prev.includes(taskId) 
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  const selectAllTasks = (taskList) => {
    const taskIds = taskList.map(t => t.id);
    const allSelected = taskIds.every(id => selectedTasks.includes(id));
    if (allSelected) {
      setSelectedTasks(prev => prev.filter(id => !taskIds.includes(id)));
    } else {
      setSelectedTasks(prev => [...new Set([...prev, ...taskIds])]);
    }
  };

  const handleDeleteSelectedTasks = async () => {
    if (selectedTasks.length === 0) return;
    
    setDeleting(true);
    try {
      let successCount = 0;
      let failCount = 0;
      
      for (const id of selectedTasks) {
        try {
          await axios.delete(`${API}/branch/tasks/${id}`, { headers: getAuthHeaders() });
          successCount++;
        } catch (error) {
          failCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`${successCount} görev silindi`);
        loadData();
      }
      if (failCount > 0) {
        toast.error(`${failCount} görev silinemedi`);
      }
      
      setSelectedTasks([]);
      setShowDeleteModal(false);
    } catch (error) {
      toast.error('Silme işlemi sırasında hata oluştu');
    } finally {
      setDeleting(false);
    }
  };

  const handleAcceptSchedule = async (scheduleId) => {
    try {
      await axios.post(`${API}/work/schedules/${scheduleId}/accept`, {}, { headers: getAuthHeaders() });
      toast.success('İş planı kabul edildi');
      loadSchedules();
    } catch (error) {
      toast.error('İş planı kabul edilemedi');
    }
  };

  const loadProgressLogs = async (scheduleId) => {
    try {
      const res = await axios.get(`${API}/work/schedules/${scheduleId}/progress`, { headers: getAuthHeaders() });
      setProgressLogs(res.data.progress_logs || []);
    } catch (error) {
      console.error('Güncel durum yüklenemedi:', error);
    }
  };

  const handleOpenProgressModal = (schedule) => {
    setSelectedSchedule(schedule);
    loadProgressLogs(schedule.id);
    setProgressModalOpen(true);
  };

  const handleProgressDocUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        setNewProgressLog(prev => ({
          ...prev,
          documents: [...prev.documents, {
            name: file.name,
            type: file.type,
            size: file.size,
            data: reader.result
          }]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const handleAddProgressLog = async () => {
    if (!newProgressLog.description.trim()) {
      toast.error('Açıklama gerekli');
      return;
    }
    try {
      await axios.post(`${API}/work/schedules/${selectedSchedule.id}/progress`, newProgressLog, { headers: getAuthHeaders() });
      toast.success('Güncel durum eklendi');
      setNewProgressLog({ description: '', documents: [] });
      loadProgressLogs(selectedSchedule.id);
      loadSchedules();
    } catch (error) {
      toast.error('Güncel durum eklenemedi');
    }
  };

  const getScheduleStatusBadge = (status) => {
    const statusMap = {
      planned: { label: 'Planlandı', class: 'bg-blue-100 text-blue-700' },
      in_progress: { label: 'Devam Ediyor', class: 'bg-amber-100 text-amber-700' },
      completed: { label: 'Tamamlandı', class: 'bg-green-100 text-green-700' },
      cancelled: { label: 'İptal Edildi', class: 'bg-red-100 text-red-700' }
    };
    const s = statusMap[status] || { label: status, class: 'bg-gray-100 text-gray-700' };
    return <Badge className={s.class}>{s.label}</Badge>;
  };
  
  const getSchedulePriorityBadge = (priority) => {
    const priorityMap = {
      low: { label: 'Düşük', class: 'bg-gray-100 text-gray-700' },
      normal: { label: 'Normal', class: 'bg-blue-100 text-blue-700' },
      high: { label: 'Yüksek', class: 'bg-orange-100 text-orange-700' },
      urgent: { label: 'Acil', class: 'bg-red-100 text-red-700' }
    };
    const p = priorityMap[priority] || { label: priority, class: 'bg-gray-100 text-gray-700' };
    return <Badge className={p.class}>{p.label}</Badge>;
  };

  const filteredSchedules = schedules.filter(s => s.schedule_type === scheduleTab);
  
  // İş Programı sıralama fonksiyonları
  const handleScheduleSort = (key) => {
    setScheduleSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };
  
  const getScheduleSortIcon = (key) => {
    if (scheduleSortConfig.key !== key) return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    return scheduleSortConfig.direction === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-amber-600" />
      : <ArrowDown className="h-4 w-4 text-amber-600" />;
  };
  
  // Sıralanmış İş Programları
  const sortedSchedules = useMemo(() => {
    if (!scheduleSortConfig.key) return filteredSchedules;
    
    return [...filteredSchedules].sort((a, b) => {
      let aValue, bValue;
      
      switch (scheduleSortConfig.key) {
        case 'title':
          aValue = (a.title || '').toLowerCase();
          bValue = (b.title || '').toLowerCase();
          break;
        case 'owner_name':
          aValue = (a.owner_name || '').toLowerCase();
          bValue = (b.owner_name || '').toLowerCase();
          break;
        case 'created_at':
          aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
          bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
        case 'date_range':
          aValue = a.start_date || '';
          bValue = b.start_date || '';
          break;
        case 'priority':
          const priorityOrder = { urgent: 4, high: 3, normal: 2, low: 1 };
          aValue = priorityOrder[a.priority] || 0;
          bValue = priorityOrder[b.priority] || 0;
          break;
        case 'status':
          const statusOrder = { in_progress: 3, planned: 2, completed: 1, cancelled: 0 };
          aValue = statusOrder[a.status] || 0;
          bValue = statusOrder[b.status] || 0;
          break;
        case 'updated_at':
          aValue = a.updated_at ? new Date(a.updated_at).getTime() : 0;
          bValue = b.updated_at ? new Date(b.updated_at).getTime() : 0;
          break;
        case 'completed_at':
          aValue = a.completion_date ? new Date(a.completion_date).getTime() : 0;
          bValue = b.completion_date ? new Date(b.completion_date).getTime() : 0;
          break;
        default:
          return 0;
      }
      
      if (typeof aValue === 'string') {
        return scheduleSortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue, 'tr')
          : bValue.localeCompare(aValue, 'tr');
      }
      
      return scheduleSortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
  }, [filteredSchedules, scheduleSortConfig]);

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error('Görev başlığı gerekli');
      return;
    }
    if (!formData.assigned_to) {
      toast.error('Görev atanacak kişi seçilmeli');
      return;
    }

    try {
      const taskData = {
        title: formData.title,
        description: formData.description,
        assigned_to: formData.assigned_to,
        task_type: formData.task_type,
        subcontractor_ids: formData.subcontractor_ids,
        start_date: formData.start_date,
        start_time: formData.start_time,
        end_date: formData.end_date,
        end_time: formData.end_time,
        is_recurring: formData.is_recurring,
        documents: formData.documents
      };

      if (editingTask) {
        await axios.put(`${API}/branch/daily-tasks/${editingTask.id}`, taskData, { headers: getAuthHeaders() });
        toast.success('Görev güncellendi');
      } else {
        await axios.post(`${API}/branch/daily-tasks`, taskData, { headers: getAuthHeaders() });
        toast.success('Günlük mesai takibi görevi oluşturuldu');
      }
      setIsOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : (Array.isArray(error.response?.data?.detail) ? error.response.data.detail.map(e => e.msg).join(', ') : 'İşlem başarısız'));
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setFormData({
      title: task.title || '',
      description: task.description || '',
      assigned_to: task.assigned_to || '',
      task_type: task.task_type || 'attendance_tracking',
      subcontractor_ids: task.subcontractor_ids || [],
      start_date: task.start_date || '',
      start_time: task.start_time || '',
      end_date: task.end_date || '',
      end_time: task.end_time || '',
      is_recurring: task.is_recurring || false,
      priority: task.priority || 'normal',
      documents: task.documents || []
    });
    setIsOpen(true);
  };

  const handleDelete = async (taskId, isDaily = false) => {
    if (!window.confirm('Bu görevi silmek istediğinize emin misiniz?')) return;
    
    try {
      const endpoint = isDaily ? `${API}/branch/daily-tasks/${taskId}` : `${API}/branch/tasks/${taskId}`;
      await axios.delete(endpoint);
      toast.success('Görev silindi');
      loadData();
    } catch (error) {
      toast.error('Görev silinemedi');
    }
  };

  const handleStopRecurring = async (taskId) => {
    if (!window.confirm('Tekrarlanan görevi durdurmak istediğinize emin misiniz?')) return;
    
    try {
      await axios.post(`${API}/branch/daily-tasks/${taskId}/stop`);
      toast.success('Tekrarlanan görev durduruldu');
      loadData();
    } catch (error) {
      toast.error('Görev durdurulamadı');
    }
  };

  const handleReview = (task) => {
    setReviewingTask(task);
    setHrNotes('');
    setRejectReason('');
    setReviewModalOpen(true);
  };

  const handleApprove = async () => {
    try {
      await axios.post(`${API}/branch/task-instances/${reviewingTask.id}/approve`, null, {
        params: { hr_notes: hrNotes }
      });
      toast.success('Görev onaylandı ve İK\'ya iletildi');
      setReviewModalOpen(false);
      loadData();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : (Array.isArray(error.response?.data?.detail) ? error.response.data.detail.map(e => e.msg).join(', ') : 'Onaylama başarısız'));
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Red nedeni belirtmelisiniz');
      return;
    }
    try {
      await axios.post(`${API}/branch/task-instances/${reviewingTask.id}/reject`, null, {
        params: { reason: rejectReason }
      });
      toast.success('Görev geri gönderildi');
      setReviewModalOpen(false);
      loadData();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : (Array.isArray(error.response?.data?.detail) ? error.response.data.detail.map(e => e.msg).join(', ') : 'Red işlemi başarısız'));
    }
  };

  const resetForm = () => {
    setEditingTask(null);
    setFormData({
      title: '',
      description: '',
      assigned_to: '',
      task_type: 'attendance_tracking',
      subcontractor_ids: [],
      start_date: '',
      start_time: '',
      end_date: '',
      end_time: '',
      is_recurring: false,
      priority: 'normal'
    });
  };

  const toggleSubcontractor = (subId) => {
    setFormData(prev => ({
      ...prev,
      subcontractor_ids: prev.subcontractor_ids.includes(subId)
        ? prev.subcontractor_ids.filter(id => id !== subId)
        : [...prev.subcontractor_ids, subId]
    }));
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { label: 'Bekliyor', color: 'bg-amber-100 text-amber-700', icon: Clock },
      in_progress: { label: 'Devam Ediyor', color: 'bg-blue-100 text-blue-700', icon: AlertCircle },
      completed: { label: 'Tamamlandı', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      submitted: { label: 'Onay Bekliyor', color: 'bg-purple-100 text-purple-700', icon: Send },
      approved: { label: 'Onaylandı', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
      rejected: { label: 'Geri Gönderildi', color: 'bg-red-100 text-red-700', icon: XCircle },
      sent_to_hr: { label: 'İK\'ya İletildi', color: 'bg-teal-100 text-teal-700', icon: Send }
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return <Badge className={`${c.color} gap-1`}><Icon className="h-3 w-3" />{c.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="branch-tasks">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Görev Yönetimi</h1>
          <p className="text-gray-500">{user?.branch_name} şubesi görevleri</p>
        </div>
        {/* Mesai Takip Görevi butonu Mesai Takibi sayfasına taşındı */}
        <Dialog open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (!open) resetForm(); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingTask ? 'Görevi Düzenle' : 'Günlük Mesai Takibi Görevi Oluştur'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div>
                <Label htmlFor="title">Görev Başlığı*</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Günlük Mesai Takibi"
                />
              </div>
              
              <div>
                <Label htmlFor="description">Açıklama</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Görev açıklaması"
                  rows={2}
                />
              </div>

              <div>
                <Label>Görev Atanacak Kişi*</Label>
                <Select
                  value={formData.assigned_to}
                  onValueChange={(val) => setFormData({...formData, assigned_to: val})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Kişi seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Subcontractor Selection */}
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Building2 className="h-4 w-4" />
                  Mesai Takibi Yapılacak Şirketler
                </Label>
                <div className="border rounded-lg p-3 space-y-2 max-h-40 overflow-y-auto">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="fortis"
                      checked={formData.subcontractor_ids.includes('fortis')}
                      onCheckedChange={() => toggleSubcontractor('fortis')}
                    />
                    <label htmlFor="fortis" className="text-sm font-medium cursor-pointer">
                      FORTİS (Ana Şirket)
                    </label>
                  </div>
                  {subcontractors.map(sub => (
                    <div key={sub.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={sub.id}
                        checked={formData.subcontractor_ids.includes(sub.id)}
                        onCheckedChange={() => toggleSubcontractor(sub.id)}
                      />
                      <label htmlFor={sub.id} className="text-sm cursor-pointer">
                        {sub.name}
                      </label>
                    </div>
                  ))}
                </div>
                {formData.subcontractor_ids.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.subcontractor_ids.length} şirket seçildi
                  </p>
                )}
              </div>

              {/* Date and Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start_date">Başlangıç Tarihi</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="start_time">Başlangıç Saati</Label>
                  <Input
                    id="start_time"
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="end_date">Bitiş Tarihi</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="end_time">Bitiş Saati</Label>
                  <Input
                    id="end_time"
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({...formData, end_time: e.target.value})}
                  />
                </div>
              </div>

              {/* Recurring Option */}
              <div className="flex items-center space-x-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <Checkbox
                  id="is_recurring"
                  checked={formData.is_recurring}
                  onCheckedChange={(checked) => setFormData({...formData, is_recurring: checked})}
                />
                <div>
                  <label htmlFor="is_recurring" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                    <RefreshCw className="h-4 w-4 text-amber-600" />
                    Tekrarlanan Günlük Görev
                  </label>
                  <p className="text-xs text-gray-500">İptal edilene kadar her gün otomatik olarak görev oluşturulur</p>
                </div>
              </div>

              {/* Documents Section */}
              <div className="space-y-2">
                <Label className="text-gray-700 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Belgeler
                </Label>
                <div className="flex flex-wrap gap-2">
                  {formData.documents.map((doc, index) => (
                    <div key={index} className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-lg">
                      <FileText className="h-4 w-4 text-blue-500" />
                      <span className="text-sm">{doc.name}</span>
                      <span className="text-xs text-gray-500">({formatFileSize(doc.size)})</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                        onClick={() => removeTaskDocument(index)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    id="task-file-upload"
                    className="hidden"
                    multiple
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                    onChange={handleTaskFileUpload}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => document.getElementById('task-file-upload').click()}
                    className="gap-2"
                  >
                    <Upload className="h-4 w-4" />
                    Belge Ekle
                  </Button>
                  <span className="text-xs text-gray-500">Max 10MB, PDF/Word/Excel/Resim</span>
                </div>
              </div>

              <Button onClick={handleSubmit} className="w-full bg-amber-600 hover:bg-amber-700">
                {editingTask ? 'Güncelle' : 'Görev Oluştur'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Main Tabs: GÖREVLER, RAPORLAR, İŞ PROGRAMI */}
      <Tabs value={mainTab} onValueChange={setMainTab} className="w-full mb-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="tasks" className="gap-2 text-base font-semibold">
            <ClipboardList className="h-5 w-5" />
            GÖREVLER
          </TabsTrigger>
          <TabsTrigger value="reports" className="gap-2 text-base font-semibold">
            <FileText className="h-5 w-5" />
            RAPORLAR
          </TabsTrigger>
          <TabsTrigger value="schedules" className="gap-2 text-base font-semibold">
            <CalendarDays className="h-5 w-5" />
            İŞ PROGRAMI
          </TabsTrigger>
        </TabsList>

        {/* GÖREVLER Tab Content */}
        <TabsContent value="tasks">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList>
              <TabsTrigger value="daily" className="gap-2">
                <Calendar className="h-4 w-4" />
                Günlük Mesai Görevleri ({dailyTasks.length})
              </TabsTrigger>
              <TabsTrigger value="internal" className="gap-2" data-testid="tab-internal">
                <Users className="h-4 w-4" />
                Şube İçi Görevler ({internalTasks.length})
              </TabsTrigger>
              <TabsTrigger value="submitted" className="gap-2">
                <Send className="h-4 w-4" />
                Onay Bekleyenler ({submittedTasks.length})
              </TabsTrigger>
              <TabsTrigger value="regular" className="gap-2">
                <ClipboardList className="h-4 w-4" />
                Diğer Görevler ({tasks.length})
              </TabsTrigger>
            </TabsList>

        {/* Daily Tasks Tab */}
        <TabsContent value="daily" className="mt-4">
          {dailyTasks.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Calendar className="h-16 w-16 mx-auto mb-4 text-amber-200" />
                <h3 className="text-xl font-bold mb-2">Günlük Mesai Görevi Yok</h3>
                <p className="text-gray-500">Henüz günlük mesai takip görevi oluşturmadınız</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                {/* Seçim Toolbar */}
                <div className="flex items-center justify-between p-3 border-b">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={dailyTasks.length > 0 && dailyTasks.every(t => selectedTasks.includes(t.id))}
                      onCheckedChange={() => selectAllTasks(dailyTasks)}
                    />
                    <span className="text-sm text-gray-500">
                      {selectedTasks.filter(id => dailyTasks.some(t => t.id === id)).length > 0 
                        ? `${selectedTasks.filter(id => dailyTasks.some(t => t.id === id)).length} seçili` 
                        : 'Tümünü seç'}
                    </span>
                  </div>
                  {selectedTasks.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setSelectedTasks([])}>
                        Seçimi Temizle
                      </Button>
                      <Button variant="destructive" size="sm" onClick={() => setShowDeleteModal(true)} className="gap-1">
                        <Trash2 className="h-4 w-4" />
                        Sil ({selectedTasks.length})
                      </Button>
                    </div>
                  )}
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Başlık</TableHead>
                      <TableHead>Atanan</TableHead>
                      <TableHead>Şirketler</TableHead>
                      <TableHead>Tarih/Saat</TableHead>
                      <TableHead>Tekrar</TableHead>
                      <TableHead>Durum</TableHead>
                      <TableHead>İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dailyTasks.map(task => (
                      <TableRow 
                        key={task.id}
                        className={selectedTasks.includes(task.id) ? 'bg-amber-50' : ''}
                      >
                        <TableCell>
                          <Checkbox
                            checked={selectedTasks.includes(task.id)}
                            onCheckedChange={() => toggleTaskSelection(task.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{task.title}</p>
                            {task.description && (
                              <p className="text-sm text-gray-500 truncate max-w-[150px]">
                                {task.description}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{task.assigned_to_name || '-'}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(task.subcontractor_names || []).map((name, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                {name}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {task.start_date && <div>{task.start_date} {task.start_time}</div>}
                            {task.end_date && <div className="text-gray-500">→ {task.end_date} {task.end_time}</div>}
                          </div>
                        </TableCell>
                        <TableCell>
                          {task.is_recurring ? (
                            <Badge className="bg-amber-100 text-amber-700 gap-1">
                              <RefreshCw className="h-3 w-3" />
                              Günlük
                            </Badge>
                          ) : (
                            <Badge variant="outline">Tek Seferlik</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {task.is_active ? (
                            <Badge className="bg-green-100 text-green-700">Aktif</Badge>
                          ) : (
                            <Badge className="bg-gray-100 text-gray-700">Durduruldu</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleViewTaskDetail(task)} title="Detay">
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(task)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            {task.is_recurring && task.is_active && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-orange-500"
                                onClick={() => handleStopRecurring(task.id)}
                                title="Tekrarı Durdur"
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-red-500"
                              onClick={() => handleDelete(task.id, true)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Şube İçi Görevler Tab - Internal Branch Tasks */}
        <TabsContent value="internal" className="mt-4">
          {internalTasks.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Users className="h-16 w-16 mx-auto mb-4 text-blue-200" />
                <h3 className="text-xl font-bold mb-2">Şube İçi Görev Yok</h3>
                <p className="text-gray-500">Şube yöneticileri arasında henüz görevlendirme yapılmamış</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-700">
                  <Users className="h-5 w-5" />
                  Şube İçi Görevler - Güncel Durumlar
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-bold">Görev</TableHead>
                      <TableHead className="font-bold">Oluşturan</TableHead>
                      <TableHead className="font-bold">Atanan</TableHead>
                      <TableHead className="font-bold">Öncelik</TableHead>
                      <TableHead className="font-bold">Tarih</TableHead>
                      <TableHead className="font-bold">Durum</TableHead>
                      <TableHead className="font-bold">Son Güncelleme</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {internalTasks.map(task => {
                      const statusConfig = {
                        pending: { label: 'Bekliyor', class: 'bg-amber-100 text-amber-700' },
                        in_progress: { label: 'Devam Ediyor', class: 'bg-blue-100 text-blue-700' },
                        completed: { label: 'Tamamlandı', class: 'bg-green-100 text-green-700' },
                        submitted: { label: 'Onay Bekliyor', class: 'bg-purple-100 text-purple-700' },
                        approved: { label: 'Onaylandı', class: 'bg-emerald-100 text-emerald-700' },
                        rejected: { label: 'Reddedildi', class: 'bg-red-100 text-red-700' },
                        cancelled: { label: 'İptal', class: 'bg-gray-100 text-gray-700' }
                      };
                      const priorityConfig = {
                        low: { label: 'Düşük', class: 'bg-gray-100 text-gray-600' },
                        normal: { label: 'Normal', class: 'bg-blue-50 text-blue-600' },
                        high: { label: 'Yüksek', class: 'bg-orange-100 text-orange-700' },
                        urgent: { label: 'Acil', class: 'bg-red-100 text-red-700' }
                      };
                      const s = statusConfig[task.status] || statusConfig.pending;
                      const p = priorityConfig[task.priority] || priorityConfig.normal;
                      
                      return (
                        <TableRow key={task.id} className="hover:bg-gray-50">
                          <TableCell>
                            <div>
                              <p className="font-medium">{task.title}</p>
                              {task.description && (
                                <p className="text-sm text-gray-500 line-clamp-1">{task.description}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-gray-400" />
                              <span>{task.created_by_name || task.owner_name || '-'}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-blue-400" />
                              <span>{task.assigned_to_name || '-'}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={p.class}>{p.label}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              {task.due_date || task.start_date || '-'}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={s.class}>{s.label}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm text-gray-500">
                              {task.updated_at 
                                ? new Date(task.updated_at).toLocaleDateString('tr-TR', { 
                                    day: '2-digit', month: '2-digit', year: 'numeric',
                                    hour: '2-digit', minute: '2-digit'
                                  })
                                : task.created_at 
                                  ? new Date(task.created_at).toLocaleDateString('tr-TR', { 
                                      day: '2-digit', month: '2-digit', year: 'numeric',
                                      hour: '2-digit', minute: '2-digit'
                                    })
                                  : '-'
                              }
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Submitted Tasks Tab - Manager Review */}
        <TabsContent value="submitted" className="mt-4">
          {submittedTasks.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <CheckCircle className="h-16 w-16 mx-auto mb-4 text-green-200" />
                <h3 className="text-xl font-bold mb-2">Onay Bekleyen Görev Yok</h3>
                <p className="text-gray-500">Tüm görevler onaylandı veya henüz tamamlanmadı</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-700">
                  <Send className="h-5 w-5" />
                  Çalışanların Tamamladığı Görevler - Onay Bekliyor
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Görev</TableHead>
                      <TableHead>Çalışan</TableHead>
                      <TableHead>Tarih</TableHead>
                      <TableHead>Gönderilme</TableHead>
                      <TableHead>Durum</TableHead>
                      <TableHead>İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {submittedTasks.map(task => (
                      <TableRow key={task.id}>
                        <TableCell className="font-medium">{task.task_title}</TableCell>
                        <TableCell>{task.employee_name}</TableCell>
                        <TableCell>{task.date}</TableCell>
                        <TableCell>
                          {task.submitted_at 
                            ? new Date(task.submitted_at).toLocaleString('tr-TR') 
                            : '-'}
                        </TableCell>
                        <TableCell>{getStatusBadge(task.status)}</TableCell>
                        <TableCell>
                          <Button 
                            size="sm" 
                            className="gap-1 bg-amber-600 hover:bg-amber-700"
                            onClick={() => handleReview(task)}
                          >
                            <Eye className="h-4 w-4" />
                            İncele
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Regular Tasks Tab */}
        <TabsContent value="regular" className="mt-4">
          {tasks.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <ClipboardList className="h-16 w-16 mx-auto mb-4 text-gray-200" />
                <h3 className="text-xl font-bold mb-2">Diğer Görev Yok</h3>
                <p className="text-gray-500">Henüz başka görev oluşturmadınız</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Başlık</TableHead>
                      <TableHead>Atanan</TableHead>
                      <TableHead>Öncelik</TableHead>
                      <TableHead>Son Tarih</TableHead>
                      <TableHead>Durum</TableHead>
                      <TableHead>İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tasks.map(task => (
                      <TableRow key={task.id} className={unreadTaskIds.has(task.id) ? 'bg-blue-50' : ''}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <span>{task.title}</span>
                            {unreadTaskIds.has(task.id) && (
                              <Badge className="bg-blue-500 text-white text-xs animate-pulse">YENİ</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{employees.find(e => e.id === task.assigned_to)?.name || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{task.priority || 'Normal'}</Badge>
                        </TableCell>
                        <TableCell>{task.due_date || '-'}</TableCell>
                        <TableCell>{getStatusBadge(task.status)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                // YENİ badge için bildirim okundu işaretle
                                if (unreadTaskIds.has(task.id)) {
                                  markTaskNotificationAsRead(task.id);
                                }
                              }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-red-500"
                              onClick={() => handleDelete(task.id, false)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
          </Tabs>
        </TabsContent>

        {/* RAPORLAR Tab Content */}
        <TabsContent value="reports">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-amber-600" />
                Şube Raporları
              </CardTitle>
            </CardHeader>
            <CardContent className="text-center py-12">
              <FileText className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-xl font-bold mb-2 text-gray-600">Raporlar</h3>
              <p className="text-gray-500">Şube raporları burada görüntülenecek</p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* İŞ PROGRAMI Tab Content */}
        <TabsContent value="schedules">
          <div className="space-y-4">
            {/* Schedule Type Tabs */}
            <Tabs value={scheduleTab} onValueChange={setScheduleTab}>
              <div className="flex justify-between items-center mb-4">
                <TabsList>
                  <TabsTrigger value="daily" className="gap-2">
                    <CalendarDays className="h-4 w-4" />
                    GÜNLÜK İŞ PLANI
                  </TabsTrigger>
                  <TabsTrigger value="monthly" className="gap-2">
                    <CalendarRange className="h-4 w-4" />
                    AYLIK İŞ PLANI
                  </TabsTrigger>
                  <TabsTrigger value="master" className="gap-2">
                    <Layers className="h-4 w-4" />
                    MASTER İŞ PLANI
                  </TabsTrigger>
                </TabsList>
                <Button onClick={() => setCreateScheduleModalOpen(true)} className="bg-amber-600 hover:bg-amber-700">
                  <Plus className="h-4 w-4 mr-2" />
                  İş Planı Ekle
                </Button>
              </div>

              {/* Schedules List - Table View */}
              <Card>
                <CardContent className="p-0">
                  {filteredSchedules.length === 0 ? (
                    <div className="text-center py-12">
                      <CalendarDays className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                      <h3 className="text-xl font-bold mb-2 text-gray-600">
                        {scheduleTab === 'daily' ? 'Günlük' : scheduleTab === 'monthly' ? 'Aylık' : 'Master'} İş Planı Yok
                      </h3>
                      <p className="text-gray-500">Henüz bu kategoride iş planı oluşturulmamış</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead 
                            className="cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleScheduleSort('title')}
                          >
                            <div className="flex items-center gap-1">
                              İş Planı
                              {getScheduleSortIcon('title')}
                            </div>
                          </TableHead>
                          <TableHead 
                            className="cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleScheduleSort('owner_name')}
                          >
                            <div className="flex items-center gap-1">
                              Oluşturan
                              {getScheduleSortIcon('owner_name')}
                            </div>
                          </TableHead>
                          <TableHead 
                            className="cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleScheduleSort('created_at')}
                          >
                            <div className="flex items-center gap-1">
                              Oluşturulma
                              {getScheduleSortIcon('created_at')}
                            </div>
                          </TableHead>
                          <TableHead 
                            className="cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleScheduleSort('date_range')}
                          >
                            <div className="flex items-center gap-1">
                              Tarih Aralığı
                              {getScheduleSortIcon('date_range')}
                            </div>
                          </TableHead>
                          <TableHead 
                            className="cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleScheduleSort('priority')}
                          >
                            <div className="flex items-center gap-1">
                              Öncelik
                              {getScheduleSortIcon('priority')}
                            </div>
                          </TableHead>
                          <TableHead 
                            className="cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleScheduleSort('status')}
                          >
                            <div className="flex items-center gap-1">
                              Durum
                              {getScheduleSortIcon('status')}
                            </div>
                          </TableHead>
                          <TableHead 
                            className="cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleScheduleSort('updated_at')}
                          >
                            <div className="flex items-center gap-1">
                              Son Güncelleme
                              {getScheduleSortIcon('updated_at')}
                            </div>
                          </TableHead>
                          <TableHead 
                            className="cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleScheduleSort('completed_at')}
                          >
                            <div className="flex items-center gap-1">
                              Tamamlanma Zamanı
                              {getScheduleSortIcon('completed_at')}
                            </div>
                          </TableHead>
                          <TableHead className="text-right">İşlemler</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortedSchedules.map(schedule => (
                          <TableRow 
                            key={schedule.id}
                            className={`cursor-pointer hover:bg-gray-50 ${unreadScheduleIds.has(schedule.id) ? 'bg-blue-50' : ''}`}
                            onClick={() => { 
                              setSelectedSchedule(schedule); 
                              setDetailModalOpen(true); 
                              // YENİ badge için bildirim okundu işaretle
                              if (unreadScheduleIds.has(schedule.id)) {
                                markScheduleNotificationAsRead(schedule.id);
                              }
                            }}
                          >
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div>
                                  <div className="font-medium flex items-center gap-2">
                                    {schedule.title}
                                    {unreadScheduleIds.has(schedule.id) && (
                                      <Badge className="bg-blue-500 text-white text-xs animate-pulse">YENİ</Badge>
                                    )}
                                  </div>
                                  {schedule.description && (
                                    <div className="text-sm text-gray-500 line-clamp-1">{schedule.description}</div>
                                  )}
                                </div>
                                {schedule.documents?.length > 0 && (
                                  <Badge variant="outline" className="gap-1">
                                    <FileText className="h-3 w-3" />
                                    {schedule.documents.length}
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">{schedule.owner_name || 'Bilinmiyor'}</div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm text-gray-500">
                                {schedule.created_at 
                                  ? new Date(schedule.created_at).toLocaleDateString('tr-TR', { 
                                      day: '2-digit', month: '2-digit', year: 'numeric', 
                                      hour: '2-digit', minute: '2-digit' 
                                    })
                                  : '-'}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                {schedule.start_date && schedule.end_date ? (
                                  <span>{schedule.start_date} - {schedule.end_date}</span>
                                ) : schedule.start_date ? (
                                  <span>{schedule.start_date}</span>
                                ) : '-'}
                              </div>
                            </TableCell>
                            <TableCell>
                              {getSchedulePriorityBadge(schedule.priority)}
                            </TableCell>
                            <TableCell>
                              {getScheduleStatusBadge(schedule.status)}
                            </TableCell>
                            <TableCell>
                              {schedule.updated_at ? (
                                <div className="text-sm text-blue-600 font-medium">
                                  {new Date(schedule.updated_at).toLocaleDateString('tr-TR', { 
                                    day: '2-digit', month: '2-digit', year: 'numeric', 
                                    hour: '2-digit', minute: '2-digit' 
                                  })}
                                </div>
                              ) : (
                                <span className="text-gray-400 text-sm">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {schedule.status === 'completed' && schedule.completion_date ? (
                                <div className="text-sm text-green-600">
                                  {new Date(schedule.completion_date).toLocaleDateString('tr-TR', { 
                                    day: '2-digit', month: '2-digit', year: 'numeric', 
                                    hour: '2-digit', minute: '2-digit' 
                                  })}
                                </div>
                              ) : (
                                <span className="text-gray-400 text-sm">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => { 
                                    e.stopPropagation(); 
                                    setSelectedSchedule(schedule); 
                                    setDetailModalOpen(true);
                                    // YENİ badge için bildirim okundu işaretle
                                    if (unreadScheduleIds.has(schedule.id)) {
                                      markScheduleNotificationAsRead(schedule.id);
                                    }
                                  }}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                {schedule.owner_id === user?.id && (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={(e) => { e.stopPropagation(); setSelectedSchedule(schedule); setEditScheduleModalOpen(true); }}
                                    >
                                      <Edit className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-red-500 hover:text-red-700"
                                      onClick={(e) => { e.stopPropagation(); handleDeleteSchedule(schedule.id); }}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </Tabs>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Schedule Modal */}
      <Dialog open={createScheduleModalOpen} onOpenChange={setCreateScheduleModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Yeni İş Planı Oluştur</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Başlık *</Label>
              <Input
                value={newSchedule.title}
                onChange={(e) => setNewSchedule({ ...newSchedule, title: e.target.value })}
                placeholder="İş planı başlığı"
              />
            </div>
            <div>
              <Label>Açıklama</Label>
              <Textarea
                value={newSchedule.description}
                onChange={(e) => setNewSchedule({ ...newSchedule, description: e.target.value })}
                placeholder="İş planı açıklaması"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Başlangıç Tarihi</Label>
                <Input
                  type="date"
                  value={newSchedule.start_date}
                  onChange={(e) => setNewSchedule({ ...newSchedule, start_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Bitiş Tarihi</Label>
                <Input
                  type="date"
                  value={newSchedule.end_date}
                  onChange={(e) => setNewSchedule({ ...newSchedule, end_date: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Öncelik</Label>
              <Select value={newSchedule.priority} onValueChange={(val) => setNewSchedule({ ...newSchedule, priority: val })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Düşük</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">Yüksek</SelectItem>
                  <SelectItem value="urgent">Acil</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Belge Yükleme */}
            <div className="border rounded-lg p-3 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <Upload className="h-4 w-4 text-blue-500" />
                  Belgeler
                </Label>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleDocumentUpload}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                  />
                  <span className="text-sm text-blue-600 hover:underline">+ Belge Ekle</span>
                </label>
              </div>
              {newSchedule.documents.length > 0 && (
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {newSchedule.documents.map((doc, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded text-sm">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <span className="truncate">{doc.name}</span>
                        <span className="text-xs text-gray-400">({formatFileSize(doc.size)})</span>
                      </div>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => removeDocument(idx)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Kişilere Gönder */}
            <div className="border rounded-lg p-3 space-y-3">
              <Label className="flex items-center gap-2">
                <Send className="h-4 w-4 text-green-500" />
                Kişilere Gönder
              </Label>
              <ScrollArea className="h-32">
                <div className="space-y-1">
                  {branchUsers.map(u => (
                    <div
                      key={u.id}
                      className={`flex items-center justify-between p-2 rounded cursor-pointer ${
                        newSchedule.send_to_users.includes(u.id) ? 'bg-green-100' : 'hover:bg-gray-100'
                      }`}
                      onClick={() => toggleUserSelection(u.id)}
                    >
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <span className="text-sm">{u.name}</span>
                      </div>
                      {newSchedule.send_to_users.includes(u.id) && (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setCreateScheduleModalOpen(false)}>İptal</Button>
              <Button onClick={handleCreateSchedule} className="bg-amber-600 hover:bg-amber-700">
                <Plus className="h-4 w-4 mr-2" />
                İş Planı Ekle
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Schedule Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>İş Planı Detayı</DialogTitle>
          </DialogHeader>
          {selectedSchedule && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold">{selectedSchedule.title}</h3>
                {getScheduleStatusBadge(selectedSchedule.status)}
              </div>
              {selectedSchedule.description && (
                <p className="text-gray-600">{selectedSchedule.description}</p>
              )}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Oluşturan</p>
                  <p className="font-medium">{selectedSchedule.owner_name}</p>
                </div>
                <div>
                  <p className="text-gray-500">Oluşturulma Tarihi</p>
                  <p className="font-medium">
                    {selectedSchedule.created_at 
                      ? new Date(selectedSchedule.created_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                      : '-'}
                  </p>
                </div>
                {selectedSchedule.start_date && (
                  <div>
                    <p className="text-gray-500">Tarih Aralığı</p>
                    <p className="font-medium">{selectedSchedule.start_date} - {selectedSchedule.end_date || '...'}</p>
                  </div>
                )}
              </div>

              {/* Belgeler */}
              {selectedSchedule.documents?.length > 0 && (
                <div>
                  <Label className="text-gray-500 mb-2 block">Belgeler</Label>
                  <div className="space-y-2">
                    {selectedSchedule.documents.map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                        <div className="flex items-center gap-2">
                          <FileText className="h-5 w-5 text-blue-500" />
                          <span className="text-sm">{doc.name}</span>
                        </div>
                        <div className="flex gap-2">
                          {isPreviewable(doc.type) && (
                            <Button variant="outline" size="sm" onClick={() => setPreviewDoc(doc)}>
                              <Eye className="h-4 w-4" />
                            </Button>
                          )}
                          <Button variant="outline" size="sm" onClick={() => {
                            const link = document.createElement('a');
                            link.href = doc.data;
                            link.download = doc.name;
                            link.click();
                          }}>
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Kabul Edenler */}
              {selectedSchedule.accepted_by && selectedSchedule.accepted_by.length > 0 && (
                <div className="pt-4 border-t">
                  <Label className="text-gray-500 mb-2 block">Kabul Edenler</Label>
                  <div className="flex flex-wrap gap-2">
                    {selectedSchedule.accepted_by.map((a, idx) => (
                      <Badge key={idx} className="bg-green-100 text-green-700">
                        {a.user_name} - {new Date(a.accepted_at).toLocaleDateString('tr-TR')}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Tamamlanmış/İptal edilmiş için sadece Güncel Durum */}
              {(selectedSchedule.status === 'completed' || selectedSchedule.status === 'cancelled') && (
                <div className="pt-4 border-t">
                  <Label className="text-gray-500 mb-2 block">Geçmiş Kayıtlar</Label>
                  <Button 
                    onClick={() => {
                      setDetailModalOpen(false);
                      handleOpenProgressModal(selectedSchedule);
                    }}
                    variant="outline"
                    className="border-blue-500 text-blue-600 hover:bg-blue-50"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Güncel Durum Geçmişi
                  </Button>
                </div>
              )}

              {/* Durum Değiştirme - Aktif planlar için */}
              {selectedSchedule.status !== 'completed' && selectedSchedule.status !== 'cancelled' && (
                <div className="pt-4 border-t">
                  <Label className="text-gray-500 mb-2 block">İşlemler</Label>
                  <div className="flex gap-2 flex-wrap">
                    {/* Başlat - Herkes basabilir */}
                    {selectedSchedule.status === 'planned' && (
                      <Button 
                        onClick={() => {
                          handleAcceptSchedule(selectedSchedule.id);
                          setDetailModalOpen(false);
                        }}
                        className="bg-amber-600 hover:bg-amber-700"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Başlat
                      </Button>
                    )}
                    
                    {/* Güncel Durum - Herkes görebilir */}
                    <Button 
                      onClick={() => {
                        setDetailModalOpen(false);
                        handleOpenProgressModal(selectedSchedule);
                      }}
                      variant="outline"
                      className="border-blue-500 text-blue-600 hover:bg-blue-50"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Güncel Durum
                    </Button>
                    
                    {/* Tamamla ve İptal - Sadece oluşturan kişi */}
                    {selectedSchedule.owner_id === user?.id && selectedSchedule.status === 'in_progress' && (
                      <>
                        <Button 
                          onClick={() => handleOpenCompletionModal(selectedSchedule)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Tamamla
                        </Button>
                        <Button 
                          onClick={() => handleScheduleStatusChange(selectedSchedule.id, 'cancelled')}
                          variant="destructive"
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          İptal Et
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Review Modal */}
      <Dialog open={reviewModalOpen} onOpenChange={setReviewModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Görev İnceleme</DialogTitle>
          </DialogHeader>
          {reviewingTask && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Görev</p>
                    <p className="font-medium">{reviewingTask.task_title}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Çalışan</p>
                    <p className="font-medium">{reviewingTask.employee_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Tarih</p>
                    <p className="font-medium">{reviewingTask.date}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Gönderilme</p>
                    <p className="font-medium">
                      {reviewingTask.submitted_at 
                        ? new Date(reviewingTask.submitted_at).toLocaleString('tr-TR') 
                        : '-'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Attendance Records */}
              {reviewingTask.attendance_records?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Mesai Kayıtları</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Çalışan</TableHead>
                          <TableHead>Durum</TableHead>
                          <TableHead>Not</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {reviewingTask.attendance_records.map((rec, i) => (
                          <TableRow key={i}>
                            <TableCell>{rec.employee_name || rec.employee_id}</TableCell>
                            <TableCell>
                              <Badge className={rec.status === 'present' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                                {rec.status === 'present' ? 'Geldi' : 'Gelmedi'}
                              </Badge>
                            </TableCell>
                            <TableCell>{rec.notes || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Signature Files */}
              {reviewingTask.signature_files?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Yüklenen Dosyalar</h4>
                  <div className="flex flex-wrap gap-2">
                    {reviewingTask.signature_files.map((file, i) => (
                      <a 
                        key={i} 
                        href={file} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline text-sm"
                      >
                        Dosya {i + 1}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              {reviewingTask.notes && (
                <div>
                  <h4 className="font-medium mb-2">Çalışan Notları</h4>
                  <p className="text-gray-600 bg-gray-50 p-3 rounded">{reviewingTask.notes}</p>
                </div>
              )}

              {/* HR Notes */}
              <div>
                <Label>İK İçin Açıklama</Label>
                <Textarea
                  value={hrNotes}
                  onChange={(e) => setHrNotes(e.target.value)}
                  placeholder="İK personeline iletilecek notlar..."
                  rows={2}
                />
              </div>

              {/* Reject Reason */}
              <div>
                <Label>Geri Gönderme Nedeni (Red için gerekli)</Label>
                <Textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Düzeltilmesi gereken hatalar..."
                  rows={2}
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <Button 
                  onClick={handleApprove} 
                  className="flex-1 bg-green-600 hover:bg-green-700 gap-2"
                >
                  <CheckCircle className="h-4 w-4" />
                  Onayla ve İK'ya İlet
                </Button>
                <Button 
                  onClick={handleReject} 
                  variant="destructive"
                  className="flex-1 gap-2"
                >
                  <XCircle className="h-4 w-4" />
                  Geri Gönder
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Belge Önizleme Modal */}
      <Dialog open={!!previewDoc} onOpenChange={() => setPreviewDoc(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-blue-500" />
              {previewDoc?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-4 bg-gray-100 rounded-lg min-h-[400px] max-h-[70vh] overflow-auto">
            {previewDoc?.type?.startsWith('image/') ? (
              <img 
                src={previewDoc?.data} 
                alt={previewDoc?.name}
                className="max-w-full max-h-[65vh] object-contain rounded shadow-lg"
              />
            ) : previewDoc?.type === 'application/pdf' ? (
              <iframe
                src={previewDoc?.data}
                title={previewDoc?.name}
                className="w-full h-[65vh] border-0 rounded"
              />
            ) : (
              <p className="text-gray-500">Bu dosya türü önizlenemiyor</p>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                const link = document.createElement('a');
                link.href = previewDoc?.data;
                link.download = previewDoc?.name;
                link.click();
              }}
            >
              <Download className="h-4 w-4 mr-2" />
              İndir
            </Button>
            <Button onClick={() => setPreviewDoc(null)}>
              Kapat
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Güncel Durum Modal */}
      <Dialog open={progressModalOpen} onOpenChange={setProgressModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-500" />
              Güncel Durum - {selectedSchedule?.title}
            </DialogTitle>
          </DialogHeader>
          
          {/* Yeni Güncelleme Ekle - Sadece aktif planlar için */}
          {selectedSchedule?.status !== 'completed' && selectedSchedule?.status !== 'cancelled' && (
            <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <Plus className="h-5 w-5 text-green-600" />
                <Label className="font-semibold">Yeni Güncelleme Ekle</Label>
              </div>
              <Textarea
                value={newProgressLog.description}
                onChange={(e) => setNewProgressLog(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Ne yapıldı? Açıklama yazın..."
                rows={3}
              />
              <div className="flex items-center justify-between">
                <label className="cursor-pointer flex items-center gap-2 text-sm text-blue-600 hover:underline">
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleProgressDocUpload}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                  />
                  <Upload className="h-4 w-4" />
                  Belge/Fotoğraf Ekle
                </label>
                <Button onClick={handleAddProgressLog} className="bg-green-600 hover:bg-green-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Ekle
                </Button>
              </div>
              {newProgressLog.documents.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {newProgressLog.documents.map((doc, idx) => (
                    <Badge key={idx} variant="outline" className="gap-1">
                      <FileText className="h-3 w-3" />
                      {doc.name}
                      <button
                        onClick={() => setNewProgressLog(prev => ({
                          ...prev,
                          documents: prev.documents.filter((_, i) => i !== idx)
                        }))}
                        className="ml-1 text-red-500 hover:text-red-700"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Tamamlanmış/İptal edilmiş için bilgi mesajı */}
          {(selectedSchedule?.status === 'completed' || selectedSchedule?.status === 'cancelled') && (
            <div className="border rounded-lg p-4 bg-blue-50 text-blue-700">
              <p className="text-sm flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Bu iş planı {selectedSchedule?.status === 'completed' ? 'tamamlanmış' : 'iptal edilmiş'}. Sadece geçmiş kayıtları görüntüleyebilirsiniz.
              </p>
            </div>
          )}

          {/* Güncellemeler Listesi */}
          <div className="space-y-4 mt-4">
            <Label className="font-semibold">Güncelleme Geçmişi</Label>
            {progressLogs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>Henüz güncelleme yapılmamış</p>
              </div>
            ) : (
              <div className="space-y-3">
                {progressLogs.map((log) => (
                  <div key={log.id} className="border rounded-lg p-4 bg-white">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <span className="font-medium">{log.user_name}</span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(log.created_at).toLocaleDateString('tr-TR', {
                          day: '2-digit', month: '2-digit', year: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <p className="text-gray-700 mb-2">{log.description}</p>
                    {log.documents && log.documents.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t">
                        {log.documents.map((doc, idx) => (
                          <div key={idx} className="flex items-center gap-1 bg-gray-100 rounded px-2 py-1">
                            <FileText className="h-3 w-3 text-gray-500" />
                            <span className="text-xs">{doc.name}</span>
                            {isPreviewable(doc.type) && (
                              <button
                                onClick={() => setPreviewDoc(doc)}
                                className="text-blue-500 hover:text-blue-700 ml-1"
                              >
                                <Eye className="h-3 w-3" />
                              </button>
                            )}
                            <button
                              onClick={() => {
                                const link = document.createElement('a');
                                link.href = doc.data;
                                link.download = doc.name;
                                link.click();
                              }}
                              className="text-green-500 hover:text-green-700 ml-1"
                            >
                              <Download className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Tamamlama Modal */}
      <Dialog open={completionModalOpen} onOpenChange={setCompletionModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              İş Planını Tamamla
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="font-medium">{selectedSchedule?.title}</p>
              <p className="text-sm text-gray-500">{selectedSchedule?.description}</p>
            </div>
            
            <div className="bg-blue-50 p-3 rounded-lg text-blue-700 text-sm">
              <p className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Tamamlanma tarihi ve saati otomatik olarak kaydedilecektir.
              </p>
            </div>
            
            <div>
              <Label htmlFor="completion-notes">Tamamlama Açıklaması *</Label>
              <Textarea 
                id="completion-notes"
                value={completionData.completion_notes}
                onChange={(e) => setCompletionData(prev => ({ ...prev, completion_notes: e.target.value }))}
                placeholder="İş planının nasıl tamamlandığını açıklayın..."
                rows={4}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompletionModalOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleCompleteWithDetails}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Tamamla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Görev Silme Onay Modalı */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Görevleri Sil
            </DialogTitle>
            <DialogDescription>
              {selectedTasks.length} adet görevi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
              İptal
            </Button>
            <Button variant="destructive" onClick={handleDeleteSelectedTasks} disabled={deleting}>
              {deleting ? 'Siliniyor...' : `Evet, ${selectedTasks.length} Görevi Sil`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Görev Detay Modalı */}
      <Dialog open={taskDetailModalOpen} onOpenChange={setTaskDetailModalOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-amber-600" />
              Görev Detayı
            </DialogTitle>
          </DialogHeader>
          {viewingTaskDetail && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <Badge className={viewingTaskDetail.is_recurring ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}>
                  {viewingTaskDetail.is_recurring ? 'Tekrarlanan' : 'Günlük Görev'}
                </Badge>
                <Badge className={
                  viewingTaskDetail.status === 'completed' ? 'bg-green-100 text-green-700' :
                  viewingTaskDetail.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                  'bg-amber-100 text-amber-700'
                }>
                  {viewingTaskDetail.status === 'completed' ? 'Tamamlandı' : 
                   viewingTaskDetail.status === 'in_progress' ? 'Devam Ediyor' : 'Bekliyor'}
                </Badge>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold">{viewingTaskDetail.title}</h3>
                {viewingTaskDetail.description && (
                  <p className="text-gray-600 mt-1">{viewingTaskDetail.description}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Oluşturan</p>
                  <p className="font-medium">{viewingTaskDetail.created_by_name || 'Bilinmiyor'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Atanan Kişi</p>
                  <p className="font-medium">{viewingTaskDetail.assigned_to_name || employees.find(e => e.id === viewingTaskDetail.assigned_to)?.name || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Oluşturulma</p>
                  <p className="font-medium">
                    {viewingTaskDetail.created_at 
                      ? new Date(viewingTaskDetail.created_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                      : '-'}
                  </p>
                </div>
                {viewingTaskDetail.start_date && (
                  <div>
                    <p className="text-gray-500">Tarih Aralığı</p>
                    <p className="font-medium">{viewingTaskDetail.start_date} - {viewingTaskDetail.end_date || '...'}</p>
                  </div>
                )}
              </div>

              {/* Belgeler */}
              {viewingTaskDetail.documents && viewingTaskDetail.documents.length > 0 && (
                <div className="pt-2 border-t">
                  <Label className="text-gray-500 mb-2 block flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Ekli Belgeler ({viewingTaskDetail.documents.length})
                  </Label>
                  <div className="space-y-2">
                    {viewingTaskDetail.documents.map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                        <div className="flex items-center gap-2">
                          <FileText className="h-5 w-5 text-blue-500" />
                          <span className="text-sm">{doc.name}</span>
                          <span className="text-xs text-gray-500">({formatFileSize(doc.size)})</span>
                        </div>
                        <div className="flex gap-2">
                          {isPreviewable(doc.type) && (
                            <Button variant="outline" size="sm" onClick={() => setPreviewDoc(doc)}>
                              <Eye className="h-4 w-4" />
                            </Button>
                          )}
                          <Button variant="outline" size="sm" onClick={() => {
                            const link = document.createElement('a');
                            link.href = doc.data;
                            link.download = doc.name;
                            link.click();
                          }}>
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end pt-2">
                <Button variant="outline" onClick={() => setTaskDetailModalOpen(false)}>
                  Kapat
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Document Preview Modal */}
      <Dialog open={!!previewDoc} onOpenChange={() => setPreviewDoc(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              {previewDoc?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="flex justify-center items-center overflow-auto max-h-[70vh]">
            {previewDoc?.type?.startsWith('image/') ? (
              <img src={previewDoc.data} alt={previewDoc.name} className="max-w-full h-auto" />
            ) : previewDoc?.type === 'application/pdf' ? (
              <iframe src={previewDoc.data} className="w-full h-[70vh]" title={previewDoc.name} />
            ) : (
              <p className="text-gray-500">Önizleme desteklenmiyor</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewDoc(null)}>Kapat</Button>
            <Button onClick={() => {
              const link = document.createElement('a');
              link.href = previewDoc.data;
              link.download = previewDoc.name;
              link.click();
            }}>
              <Download className="h-4 w-4 mr-2" />
              İndir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BranchTasks;
